/**
 * The creation-flow form dialect — the vocabulary every listing step paints with.
 *
 * These constants are TOKENS, not literals. Their dark values are byte-for-byte
 * what they used to spell (`bg-muted` = `#262626` = `bg-neutral-800`,
 * `text-muted-foreground` = `#a3a3a3` = `text-neutral-400`), so the migration is
 * a visual no-op for as long as the panel still forces `dark` — and the whole
 * dialect follows the theme the moment it stops (Phase 2 step 2f).
 *
 * Rungs shared with the rest of the overlay language (steps 2b/2c), keep them
 * consistent when adding a new one:
 *   `bg-neutral-900` → `bg-sheet`          `text-white`        → `text-foreground`
 *   `bg-neutral-800` → `bg-muted`          `text-neutral-200`  → `text-foreground/90`
 *   `bg-neutral-700` → `bg-sheet-handle`   `text-neutral-300`  → `text-foreground/80`
 *   `bg-white/N`     → `bg-foreground/N`   `text-neutral-400`  → `text-muted-foreground`
 *   `border-neutral-800` → `border-border`  `text-neutral-500` → `text-muted-foreground/70`
 *
 * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §1 (token arithmetic), §4 step 2e
 * @see lib/sheet-styles.ts — `SHEET_PANEL_DARK`, the surface these sit on
 */
import { cn } from "@/lib/utils";
import {
  FIELD_SURFACE_FOCUS_VISIBLE_CLASS,
  FIELD_SURFACE_INPUT_CLASS,
  FIELD_SURFACE_NAV_CLASS,
} from "@/lib/dropdown-styles";

export const creationFlowInputClass = FIELD_SURFACE_INPUT_CLASS;

export const creationFlowLabelClass = "text-base text-foreground/90";

export const creationFlowBlockClass = "rounded-2xl bg-muted/50";

export const creationFlowMutedClass = "text-muted-foreground/70";

export const creationFlowSectionLabelClass = "text-xs font-medium uppercase tracking-wider text-muted-foreground/70";

/* `FIELD_SURFACE_NAV_CLASS` already carries the ink both ways
   (`text-foreground dark:text-white`); the explicit `text-foreground` here only
   pins the light half so the two never disagree by stylesheet order. */
export const creationFlowTextareaClass = cn(
  "min-h-[120px] p-4 text-base text-foreground placeholder:text-muted-foreground resize-none",
  FIELD_SURFACE_NAV_CLASS,
  FIELD_SURFACE_FOCUS_VISIBLE_CLASS,
);

export const creationFlowOptionRowSelectedClass =
  "rounded-xl bg-muted px-4 py-3 text-foreground";

export const creationFlowOptionRowClass =
  "rounded-xl bg-muted/50 px-4 py-3 text-foreground hover:bg-muted/70 transition-colors";

export const creationFlowButtonOutlineClass =
  "rounded-2xl bg-muted/50 text-foreground hover:bg-muted border-0";

// Primary CTA across the cart + creation-flow dialogs (49 call sites). Foil per
// the DA — the one possible action of a form/dialog is foil (see
// .tondocs/ui/FOIL.md). Ink is forced with `!` so it beats the Button default
// variant's `text-primary-foreground` (a text-* utility otherwise wins the
// cascade over the foil ink). `foil-fill` (background-image) paints over the
// variant's `bg-primary`.
// The `#171717` is deliberately a LITERAL, not `text-sheet`: the foil is gold in
// both themes, so its ink must stay dark in both. Do not tokenise it.
export const creationFlowPrimaryCtaClass =
  "w-full h-12 rounded-full text-base font-semibold overflow-hidden foil-fill foil-emboss foil-sweep text-[#171717]! hover:brightness-105 active:scale-[0.98] disabled:opacity-70 disabled:pointer-events-none";

export const creationFlowPrimaryCtaTallClass =
  "w-full py-6 rounded-full text-base font-semibold overflow-hidden foil-fill foil-emboss foil-sweep text-[#171717]! hover:brightness-105 active:scale-[0.98] disabled:opacity-70 disabled:pointer-events-none";

export const creationFlowSecondaryCtaClass =
  "w-full h-12 rounded-full text-base font-semibold border border-border bg-background hover:bg-muted active:bg-muted disabled:opacity-70 disabled:pointer-events-none";
